import React, { useState, useEffect, useCallback } from 'react';
import {
    addParticipant, updateParticipant, updateParticipantRole,
    resetPassword, deleteParticipant,
    getRegistrations, approveRegistration, rejectRegistration,
    getOrganizers, getParticipants, bulkDeleteParticipants,
    getTournaments, getPools
} from '../services/api';
import ParticipantMembershipsModal from '../components/ParticipantMembershipsModal';
import ConfirmDialog from '../components/ConfirmDialog';

// Top-level component: renders a subnav ("Participants" | "New Registrations
// (N)") and the corresponding panel for the active subnav. The pending
// count badge in the subnav refreshes whenever the registrations list does.

function AdminParticipants(props) {
    const [subnav,        setSubnav]        = useState('participants');
    const [pendingCount,  setPendingCount]  = useState(0);

    // Refresh just the pending count -- used to update the badge from any
    // panel that affects the count (approve, reject, or another browser
    // tab submitting a request).
    const refreshPendingCount = useCallback(async () => {
        try {
            const res = await getRegistrations('pending');
            setPendingCount(res.data.requests.length);
        } catch {
            // Non-fatal: the badge just won't show. We don't want a
            // background fetch failure to break the page.
        }
    }, []);

    useEffect(() => {
        refreshPendingCount();
    }, [refreshPendingCount]);

    return (
        <div>
            <div className="flex gap-1 mb-6 border-b border-gray-800">
                <button
                    onClick={() => setSubnav('participants')}
                    className={'px-4 py-2 text-sm font-bold transition-colors border-b-2 ' +
                        (subnav === 'participants'
                            ? 'text-orange-400 border-orange-400'
                            : 'text-gray-500 border-transparent hover:text-gray-300')}
                >
                    All Participants
                </button>
                <button
                    onClick={() => setSubnav('registrations')}
                    className={'px-4 py-2 text-sm font-bold transition-colors border-b-2 flex items-center gap-2 ' +
                        (subnav === 'registrations'
                            ? 'text-orange-400 border-orange-400'
                            : 'text-gray-500 border-transparent hover:text-gray-300')}
                >
                    <span>New Registrations</span>
                    {pendingCount > 0 && (
                        <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {pendingCount}
                        </span>
                    )}
                </button>
            </div>

            {subnav === 'participants' && <ParticipantsPanel {...props} />}
            {subnav === 'registrations' && <RegistrationsPanel
                showFeedback={props.showFeedback}
                onRefreshParticipants={props.onRefresh}
                onPendingCountChange={refreshPendingCount}
            />}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// ParticipantsPanel — preserves the original AdminParticipants content
// verbatim. No behavior changes from the pre-step-6 version.
// ─────────────────────────────────────────────────────────────────────

function ParticipantsPanel({ participants, onRefresh, showFeedback, currentUserId, isPlatformAdmin }) {
    const [newName,      setNewName]      = useState('');
    const [newEmail,     setNewEmail]     = useState('');
    const [newPassword,  setNewPassword]  = useState('');
    const [editId,        setEditId]        = useState(null);
    const [editPassword,  setEditPassword]  = useState('');
    const [editNameId,    setEditNameId]    = useState(null);
    const [editNameValue, setEditNameValue] = useState('');
    const [editRoleId,    setEditRoleId]    = useState(null);
    const [filterTournamentId, setFilterTournamentId] = useState('all');
    const [filterPoolId,       setFilterPoolId]       = useState('all');
    const [viewingParticipant, setViewingParticipant] = useState(null); // { id, name, email } | null
    const [bulkDeleteConfirm,  setBulkDeleteConfirm]  = useState(null); // { message } | null
    const [bulkDeleting,       setBulkDeleting]       = useState(false);

    // Platform-admin-only: filter the list down to one organizer's own
    // pools' participants (or "All organizers" for the original global
    // list). Overrides the `participants` prop with a locally-fetched
    // list while active -- the prop stays the source of truth for the
    // unfiltered case, matching what Admin.js already loads on mount.
    const [organizers,          setOrganizers]          = useState([]);
    const [organizerFilter,     setOrganizerFilter]     = useState('all');
    const [filteredByOrganizer, setFilteredByOrganizer] = useState(null);

    // The canonical, authoritative list of every tournament and pool in
    // scope (platform admin: everything; regular organizer: their own),
    // fetched independently of participant data. Deliberately NOT derived
    // from participants' memberships -- an empty tournament/pool (no
    // participants at all, e.g. right after a bulk delete) should still
    // show up as a selectable filter option, not silently vanish just
    // because nobody currently matches it.
    const [allTournaments, setAllTournaments] = useState([]);
    const [allPools,       setAllPools]       = useState([]);

    useEffect(() => {
        getTournaments().then(res => setAllTournaments(res.data)).catch(() => {});
        getPools().then(res => setAllPools(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!isPlatformAdmin) return;
        getOrganizers().then(res => setOrganizers(res.data)).catch(() => {});
    }, [isPlatformAdmin]);

    const refreshOrganizerFilter = useCallback((orgId) => {
        if (orgId === 'all') {
            setFilteredByOrganizer(null);
            return;
        }
        getParticipants(orgId)
            .then(res => setFilteredByOrganizer(res.data))
            .catch(err => showFeedback(err.response?.data?.error || 'Failed to load participants', 'error'));
    }, [showFeedback]);

    const handleOrganizerFilterChange = (orgId) => {
        setOrganizerFilter(orgId);
        refreshOrganizerFilter(orgId);
    };

    // After any mutation, refresh whichever view is currently active --
    // the global list (parent's onRefresh) always, plus the organizer
    // filter's own fetch if one is selected, so the two can't drift out
    // of sync with each other.
    const refreshAfterMutation = () => {
        onRefresh();
        if (organizerFilter !== 'all') refreshOrganizerFilter(organizerFilter);
    };

    const activeParticipants = filteredByOrganizer !== null ? filteredByOrganizer : participants;

    const getRoleFromParticipant = (p) => {
        if (p.is_platform_admin) return 'platform_admin';
        if (p.organizer_id && p.organizer_status === 'active') return 'organizer';
        return 'participant';
    };

    const handleRoleChange = async (p, newRole) => {
        if (getRoleFromParticipant(p) === newRole) { setEditRoleId(null); return; }
        try {
            await updateParticipantRole(p.id, newRole);
            showFeedback(`Role updated to ${newRole.replace('_', ' ')}`);
            setEditRoleId(null);
            refreshAfterMutation();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to update role', 'error');
            setEditRoleId(null);
        }
    };

    const handleAddParticipant = async (e) => {
        e.preventDefault();
        try {
            // is_admin (legacy pre-multi-tenancy flag) is intentionally
            // never set true from this form anymore -- it's dormant on
            // the backend (adminOnly middleware is no longer wired to any
            // route) and only affected Manage-tab *visibility* on the
            // frontend, not actual access, which would have shown a
            // participant a tab full of pages they had no organizer
            // context to use. Role (organizer/platform_admin) is granted
            // via the Role column below instead. Column/flag kept in the
            // DB for now, just removed from this form.
            await addParticipant(newName, newEmail, newPassword, false);
            showFeedback('Participant added successfully');
            setNewName(''); setNewEmail(''); setNewPassword('');
            refreshAfterMutation();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to add participant', 'error');
        }
    };

    const handleResetPassword = async (id) => {
        try {
            await resetPassword(id, editPassword);
            showFeedback('Password reset successfully');
            setEditId(null);
            setEditPassword('');
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to reset password', 'error');
        }
    };

    const handleUpdateName = async (id) => {
        try {
            await updateParticipant(id, editNameValue.trim());
            showFeedback('Name updated successfully');
            setEditNameId(null);
            setEditNameValue('');
            refreshAfterMutation();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to update name', 'error');
        }
    };

    const handleDeleteParticipant = async (participant, onConfirm) => {
        onConfirm({
            message: `Delete ${participant.name}? This will remove them from all pools and delete all their picks.`,
            requireTyped: false,
            onConfirm: async () => {
                try {
                    await deleteParticipant(participant.id);
                    showFeedback('Participant deleted');
                    refreshAfterMutation();
                } catch (err) {
                    showFeedback(err.response?.data?.error || 'Failed to delete participant', 'error');
                }
            }
        });
    };

    // Tournament options: every tournament in scope, narrowed to the
    // currently-selected organizer (when one is selected). Pool options:
    // every pool in scope, narrowed to the currently-selected tournament
    // (when one is selected) and, transitively, the selected organizer
    // (via each pool's own tournament_id).
    const tournamentOptions = organizerFilter === 'all'
        ? allTournaments
        : allTournaments.filter(t => t.organizer_id === parseInt(organizerFilter, 10));

    const tournamentIdsInScope = new Set(tournamentOptions.map(t => t.id));
    const poolOptions = allPools.filter(p => {
        if (filterTournamentId !== 'all') return p.tournament_id === parseInt(filterTournamentId, 10);
        return organizerFilter === 'all' || tournamentIdsInScope.has(p.tournament_id);
    });

    const handleTournamentFilterChange = (value) => {
        setFilterTournamentId(value);
        // A previously-selected pool may not belong to the newly-selected
        // tournament -- reset it rather than leave a stale, inconsistent
        // combination in place.
        setFilterPoolId('all');
    };

    const filteredParticipants = activeParticipants.filter(p => {
        if (filterTournamentId === 'all' && filterPoolId === 'all') return true;
        return (p.memberships || []).some(m =>
            (filterTournamentId === 'all' || m.tournament_id === parseInt(filterTournamentId, 10)) &&
            (filterPoolId === 'all' || m.pool_id === parseInt(filterPoolId, 10))
        );
    });

    const handleBulkDelete = () => {
        const names = filteredParticipants.map(p => p.name);
        const preview = names.length > 15
            ? names.slice(0, 15).join(', ') + `, and ${names.length - 15} more`
            : names.join(', ');
        setBulkDeleteConfirm({
            message: `You are about to permanently delete ${names.length} participant(s), ` +
                     `matching your current filters: ${preview}. This also removes their picks and ` +
                     `pool entries, and cannot be undone.`,
        });
    };

    const confirmBulkDelete = async () => {
        setBulkDeleting(true);
        try {
            const ids = filteredParticipants.map(p => parseInt(p.id, 10));
            const res = await bulkDeleteParticipants(ids);
            const { deleted, skipped } = res.data;
            if (skipped.length === 0) {
                showFeedback(`Deleted ${deleted.length} participant(s).`);
            } else {
                showFeedback(
                    `Deleted ${deleted.length} participant(s). Skipped ${skipped.length}: ` +
                    skipped.map(s => `#${s.id} (${s.reason})`).join(', ')
                );
            }
            refreshAfterMutation();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Bulk delete failed', 'error');
        } finally {
            setBulkDeleting(false);
            setBulkDeleteConfirm(null);
        }
    };

    return (
        <div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8">
                <h3 className="text-lg font-black text-white mb-4">Add participant</h3>
                <form onSubmit={handleAddParticipant} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs text-gray-400 mb-1">Name</label>
                        <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500" required />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-400 mb-1">Email</label>
                        <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500" required />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-400 mb-1">Password</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500" required />
                    </div>
                    <div className="flex items-end gap-4">
                        <button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 rounded-lg transition-colors text-sm">
                            Add participant
                        </button>
                    </div>
                </form>
            </div>

            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className="text-lg font-black text-white">All participants</h3>
                <div className="flex items-center gap-2 flex-wrap">
                    {isPlatformAdmin && (
                        <select value={organizerFilter} onChange={e => handleOrganizerFilterChange(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                            <option value="all">All organizers</option>
                            {organizers.map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
                        </select>
                    )}
                    <select value={filterTournamentId} onChange={e => handleTournamentFilterChange(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                        <option value="all">All tournaments</option>
                        {tournamentOptions.map(t => (
                            <option key={t.id} value={t.id}>{t.name} ({t.year})</option>
                        ))}
                    </select>
                    <select value={filterPoolId} onChange={e => setFilterPoolId(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                        <option value="all">All pools</option>
                        {poolOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    {filteredParticipants.length > 0 &&
                     (organizerFilter !== 'all' || filterTournamentId !== 'all' || filterPoolId !== 'all') && (
                        <button
                            onClick={handleBulkDelete}
                            className="bg-red-900/40 hover:bg-red-900/70 border border-red-800 text-red-300 text-sm font-bold px-3 py-2 rounded-lg transition-colors"
                        >
                            Delete {filteredParticipants.length} visible
                        </button>
                    )}
                </div>
            </div>

            {bulkDeleteConfirm && (
                <ConfirmDialog
                    message={bulkDeleteConfirm.message}
                    requireTyped
                    onCancel={() => setBulkDeleteConfirm(null)}
                    onConfirm={bulkDeleting ? () => {} : confirmBulkDelete}
                />
            )}

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-800">
                            <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Name</th>
                            <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Email</th>
                            <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Role</th>
                            <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Password</th>
                            <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredParticipants.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-gray-500 text-sm">
                                    No participants match the current filter
                                    {(organizerFilter !== 'all' || filterTournamentId !== 'all' || filterPoolId !== 'all') && (
                                        <>: {[
                                            organizerFilter !== 'all' && organizers.find(o => o.id === parseInt(organizerFilter, 10))?.display_name,
                                            filterTournamentId !== 'all' && tournamentOptions.find(t => t.id === parseInt(filterTournamentId, 10))?.name,
                                            filterPoolId !== 'all' && poolOptions.find(p => p.id === parseInt(filterPoolId, 10))?.name,
                                        ].filter(Boolean).join(' → ')}</>
                                    )}
                                    .
                                </td>
                            </tr>
                        )}
                        {filteredParticipants.map((p, i) => (
                            <tr key={p.id} className={'border-b border-gray-800 ' + (i % 2 === 0 ? '' : 'bg-gray-900/50')}>
                                <td className="px-4 py-3 text-white font-medium">
                                    <button
                                        onClick={() => setViewingParticipant(p)}
                                        className="hover:text-orange-400 hover:underline transition-colors text-left"
                                    >
                                        {p.name}
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-gray-400">{p.email}</td>
                                <td className="px-4 py-3">
                                    {editRoleId === p.id ? (
                                        <select
                                            autoFocus
                                            defaultValue={getRoleFromParticipant(p)}
                                            onBlur={e => handleRoleChange(p, e.target.value)}
                                            onChange={e => handleRoleChange(p, e.target.value)}
                                            className="bg-gray-800 border border-orange-500 text-white text-xs rounded px-2 py-1 focus:outline-none">
                                            <option value="participant">Participant</option>
                                            <option value="organizer">Organizer</option>
                                            <option value="platform_admin">Platform Admin</option>
                                        </select>
                                    ) : (
                                        <button
                                            onClick={() => isPlatformAdmin && currentUserId !== p.id && setEditRoleId(p.id)}
                                            title={
                                                !isPlatformAdmin ? 'Only the platform owner can change roles'
                                                : currentUserId === p.id ? 'Cannot change your own role'
                                                : 'Click to change role'
                                            }
                                            className={'text-xs px-2 py-0.5 rounded-full ' +
                                                (!isPlatformAdmin || currentUserId === p.id ? 'cursor-default ' : 'cursor-pointer hover:ring-1 hover:ring-orange-500 ') +
                                                (p.is_platform_admin
                                                    ? 'bg-orange-900 text-orange-400'
                                                    : (p.organizer_id && p.organizer_status === 'active')
                                                        ? 'bg-blue-900 text-blue-400'
                                                        : 'bg-gray-800 text-gray-500')}>
                                            {p.is_platform_admin
                                                ? 'Platform Admin'
                                                : (p.organizer_id && p.organizer_status === 'active')
                                                    ? 'Organizer'
                                                    : 'Participant'}
                                        </button>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    {p.has_password
                                        ? <span className="text-green-500 text-xs">Set</span>
                                        : <span className="text-red-500 text-xs">Not set</span>}
                                </td>
                                <td className="px-4 py-3">
                                    {editNameId === p.id ? (
                                        <div className="flex items-center gap-2">
                                            <input type="text" value={editNameValue}
                                                onChange={e => setEditNameValue(e.target.value)}
                                                placeholder="New name"
                                                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500 w-36"
                                                autoFocus />
                                            <button onClick={() => handleUpdateName(p.id)}
                                                className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-2 py-1 rounded transition-colors">Save</button>
                                            <button onClick={() => { setEditNameId(null); setEditNameValue(''); }}
                                                className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded transition-colors">Cancel</button>
                                        </div>
                                    ) : editId === p.id ? (
                                        <div className="flex items-center gap-2">
                                            <input type="password" value={editPassword}
                                                onChange={e => setEditPassword(e.target.value)}
                                                placeholder="New password"
                                                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500 w-32" />
                                            <button onClick={() => handleResetPassword(p.id)}
                                                className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-2 py-1 rounded transition-colors">Save</button>
                                            <button onClick={() => { setEditId(null); setEditPassword(''); }}
                                                className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded transition-colors">Cancel</button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            {(!isPlatformAdmin && p.shared_with_other_organizer) ? (
                                                <span
                                                    className="text-xs text-gray-600 italic"
                                                    title="This participant is also enrolled in another organizer's pool, so editing isn't allowed here. Contact the platform owner for assistance.">
                                                    Shared with another organizer — contact platform owner
                                                </span>
                                            ) : (
                                                <>
                                                    <button onClick={() => { setEditNameId(p.id); setEditNameValue(p.name); }}
                                                        className="bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-xs px-3 py-1 rounded transition-colors">
                                                        Edit name
                                                    </button>
                                                    <button onClick={() => setEditId(p.id)}
                                                        className="bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-xs px-3 py-1 rounded transition-colors">
                                                        Reset password
                                                    </button>
                                                    <button onClick={() => handleDeleteParticipant(p, (dialog) => {
                                                        if (window.confirm(dialog.message)) dialog.onConfirm();
                                                    })}
                                                        className="bg-red-900 hover:bg-red-800 text-red-400 hover:text-red-300 text-xs px-3 py-1 rounded transition-colors">
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {viewingParticipant && (
                <ParticipantMembershipsModal
                    participant={viewingParticipant}
                    onClose={() => setViewingParticipant(null)}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// RegistrationsPanel — manage pending and approved registration requests.
// New in PR-R1+R2 step 6.
// ─────────────────────────────────────────────────────────────────────

function RegistrationsPanel({ showFeedback, onRefreshParticipants, onPendingCountChange }) {
    const [filter,       setFilter]       = useState('pending');
    const [requests,     setRequests]     = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [confirmingId, setConfirmingId] = useState(null);  // id with approval confirm UI showing
    const [confirmNotes, setConfirmNotes] = useState('');
    const [actionPwd,    setActionPwd]    = useState(null);  // {id, password} when email failed

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getRegistrations(filter === 'all' ? 'all' : filter);
            setRequests(res.data.requests);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to load registrations', 'error');
        } finally {
            setLoading(false);
        }
    }, [filter, showFeedback]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const startApprove = (id) => {
        setConfirmingId(id);
        setConfirmNotes('');
        setActionPwd(null);
    };

    const cancelApprove = () => {
        setConfirmingId(null);
        setConfirmNotes('');
    };

    const confirmApprove = async (id) => {
        try {
            const res = await approveRegistration(id, confirmNotes.trim() || undefined);
            const data = res.data;
            if (data.email_sent) {
                showFeedback('Registration approved. Email sent to user.');
            } else {
                // Email failed -- show the password so admin can text it.
                setActionPwd({ id, password: data.temp_password, error: data.email_error });
            }
            setConfirmingId(null);
            setConfirmNotes('');
            await refresh();
            await onPendingCountChange();
            // The new participant now exists; refresh the parent's
            // participant list so the Participants subnav reflects them.
            if (onRefreshParticipants) onRefreshParticipants();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to approve registration', 'error');
        }
    };

    const handleReject = async (id, name) => {
        if (!window.confirm(`Reject the registration request from ${name}? The request will be deleted.`)) return;
        try {
            await rejectRegistration(id);
            showFeedback('Registration rejected');
            await refresh();
            await onPendingCountChange();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to reject registration', 'error');
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div>
            {/* Email-failure banner: stays visible until admin dismisses. */}
            {actionPwd && (
                <div className="bg-yellow-900/40 border border-yellow-600 rounded-xl p-4 mb-6">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="text-yellow-400 font-bold mb-1">
                                Approved, but email delivery failed
                            </div>
                            <div className="text-yellow-200 text-sm mb-2">
                                The participant was created and enrolled, but the email could not
                                be sent ({actionPwd.error || 'unknown error'}). Send them this
                                temporary password through another channel:
                            </div>
                            <div className="bg-gray-950 border border-gray-700 rounded px-3 py-2 font-mono text-orange-400 text-sm inline-block">
                                {actionPwd.password}
                            </div>
                        </div>
                        <button
                            onClick={() => setActionPwd(null)}
                            className="text-yellow-400 hover:text-yellow-300 text-sm"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* Filter pills */}
            <div className="flex gap-2 mb-6">
                {[
                    { id: 'pending',  label: 'Pending'  },
                    { id: 'approved', label: 'Approved' },
                    { id: 'all',      label: 'All'      },
                ].map(opt => (
                    <button
                        key={opt.id}
                        onClick={() => setFilter(opt.id)}
                        className={'px-4 py-2 text-sm font-bold rounded-full transition-colors ' +
                            (filter === opt.id
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300')}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {loading && (
                <div className="text-gray-400 text-center py-8">Loading...</div>
            )}

            {!loading && requests.length === 0 && (
                <div className="text-gray-400 text-center py-8">
                    {filter === 'pending'
                        ? 'No pending registration requests.'
                        : filter === 'approved'
                            ? 'No approved registrations yet.'
                            : 'No registration requests.'}
                </div>
            )}

            {!loading && requests.length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-800">
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Name</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Email</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Tournament</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Pools</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Submitted</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Status</th>
                                <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((r, i) => {
                                const isConfirming = confirmingId === r.id;
                                return (
                                    <tr key={r.id} className={'border-b border-gray-800 ' + (i % 2 === 0 ? '' : 'bg-gray-900/50')}>
                                        <td className="px-4 py-3 text-white font-medium">{r.name}</td>
                                        <td className="px-4 py-3 text-gray-400">{r.email}</td>
                                        <td className="px-4 py-3 text-gray-400">{r.tournament_name} ({r.tournament_year})</td>
                                        <td className="px-4 py-3 text-gray-400">{r.pool_names.join(', ')}</td>
                                        <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(r.created_at)}</td>
                                        <td className="px-4 py-3">
                                            {r.status === 'pending'
                                                ? <span className="bg-yellow-900 text-yellow-400 text-xs px-2 py-0.5 rounded-full">Pending</span>
                                                : <span className="bg-green-900 text-green-400 text-xs px-2 py-0.5 rounded-full">Approved</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            {r.status === 'approved' ? (
                                                <span className="text-gray-500 text-xs">
                                                    {r.notes ? `“${r.notes}”` : '—'}
                                                </span>
                                            ) : isConfirming ? (
                                                <div className="flex flex-col gap-2">
                                                    <input
                                                        type="text"
                                                        value={confirmNotes}
                                                        onChange={e => setConfirmNotes(e.target.value)}
                                                        placeholder="Notes (optional)"
                                                        maxLength={500}
                                                        className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500 w-full"
                                                        autoFocus
                                                    />
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => confirmApprove(r.id)}
                                                            className="bg-green-700 hover:bg-green-600 text-white text-xs px-3 py-1 rounded transition-colors"
                                                        >
                                                            Confirm approval
                                                        </button>
                                                        <button
                                                            onClick={cancelApprove}
                                                            className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded transition-colors"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => startApprove(r.id)}
                                                        className="bg-green-800 hover:bg-green-700 text-green-300 hover:text-green-200 text-xs px-3 py-1 rounded transition-colors"
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => handleReject(r.id, r.name)}
                                                        className="bg-red-900 hover:bg-red-800 text-red-400 hover:text-red-300 text-xs px-3 py-1 rounded transition-colors"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AdminParticipants;